// Timeline calculation utilities

/** Stable compare for local state ↔ Redux sync (order-independent). */
export const areTimelineEffectListsEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  const sortById = (effects) =>
    [...effects].sort((a, b) => String(a.id).localeCompare(String(b.id)));
  return JSON.stringify(sortById(left)) === JSON.stringify(sortById(right));
};

export const groupTimelineEffectsByObjectId = (effects = []) =>
  effects.reduce((acc, item) => {
    if (!acc[item.objectId]) acc[item.objectId] = [];
    acc[item.objectId].push(item);
    return acc;
  }, {});

export const calculateTimelineBounds = (timelineEffects, effectEndTime) => {
  // Find the maximum end time from all effects
  let actualMaxEndTime = 7; // Default minimum

  timelineEffects.forEach((effect) => {
    if (effect.endTime > actualMaxEndTime) {
      actualMaxEndTime = effect.endTime;
    }
  });

  // Always start from 0, but adjust the end time based on zoom and content
  const startTime = 0; // Always start from 0
  const endTime = Math.max(effectEndTime, actualMaxEndTime);

  return {
    startTime: startTime,
    endTime: endTime,
    duration: endTime - startTime,
    actualMaxEndTime: actualMaxEndTime,
  };
};

export const timeToPercent = (time, effectStartTime, effectEndTime) => {
  // Always calculate from 0 as the start, with the view duration determining the scale
  const viewDuration = effectEndTime - effectStartTime;
  if (viewDuration <= 0) return 0;

  // Calculate position relative to the view start time
  const relativeTime = time - effectStartTime;
  const percent = (relativeTime / viewDuration) * 100;
  return Math.max(0, Math.min(100, roundToPrecision(percent, 2)));
};

export const calculateTimelinePosition = (
  clientX,
  rect,
  effectStartTime,
  effectEndTime
) => {
  const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
  const timelineWidth = rect.width;
  const viewDuration = effectEndTime - effectStartTime;

  // Calculate time position relative to the view
  const relativeTimePosition = (x / timelineWidth) * viewDuration;
  const actualTimePosition = effectStartTime + relativeTimePosition;
  const positionPercent = (x / timelineWidth) * 100;

  return {
    timePosition: Math.max(0, actualTimePosition),
    positionPercent: Math.max(0, Math.min(100, positionPercent)),
  };
};

export const generateTimeMarkers = (
  effectStartTime,
  effectEndTime,
  timelineScale
) => {
  const viewDuration = effectEndTime - effectStartTime;

  let markerInterval;
  if (timelineScale >= 5) {
    markerInterval = 0.1;
  } else if (timelineScale >= 2) {
    markerInterval = 0.5;
  } else if (viewDuration <= 10) {
    markerInterval = 0.5;
  } else if (viewDuration <= 30) {
    markerInterval = 1;
  } else if (viewDuration <= 60) {
    markerInterval = 5;
  } else {
    markerInterval = 10;
  }

  const markers = [];
  // Always include the exact start
  markers.push(Number(effectStartTime.toFixed(2)));

  // Find the first marker after start
  let firstMarker =
    Math.ceil(effectStartTime / markerInterval) * markerInterval;
  // Avoid duplicate if start is exactly on interval
  if (Math.abs(firstMarker - effectStartTime) < 1e-6) {
    firstMarker += markerInterval;
  }

  for (
    let t = firstMarker;
    t < effectEndTime;
    t = Number((t + markerInterval).toFixed(6))
  ) {
    markers.push(Number(t.toFixed(2)));
  }

  // Always include the exact end if not already present
  if (
    markers.length === 0 ||
    Math.abs(markers[markers.length - 1] - effectEndTime) > 1e-6
  ) {
    markers.push(Number(effectEndTime.toFixed(2)));
  }

  return markers;
};

export const findNextAvailableRow = (
  objectId,
  startTime,
  endTime,
  timelineEffects,
  excludeEffectId = null
) => {
  const objectEffects = timelineEffects.filter(
    (effect) => effect.objectId === objectId && effect.id !== excludeEffectId
  );

  // Function to check if two time ranges overlap
  const hasTimeOverlap = (start1, end1, start2, end2) => {
    return !(end1 <= start2 || start1 >= end2);
  };

  for (let row = 0; row < 20; row++) {
    const effectsInRow = objectEffects.filter(
      (effect) => (effect.row || 0) === row
    );
    const hasOverlap = effectsInRow.some((effect) => {
      return hasTimeOverlap(
        startTime,
        endTime,
        effect.startTime,
        effect.endTime
      );
    });

    if (!hasOverlap) {
      return row;
    }
  }

  return objectEffects.length;
};

/**
 * Reorganizes effects for an object to fill rows compactly from bottom to top
 * This removes gaps in row assignments by reassigning effects to the lowest available row
 * @param {string} objectId - The object ID to reorganize effects for
 * @param {Array} timelineEffects - All timeline effects
 * @returns {Array} Updated timeline effects with reorganized rows
 */
export const reorganizeEffectRows = (objectId, timelineEffects) => {
  // Get all effects for this object
  const objectEffects = timelineEffects
    .filter((effect) => effect.objectId === objectId)
    .map((effect) => ({ ...effect })); // Create a copy to avoid mutating original

  if (objectEffects.length === 0) {
    return timelineEffects;
  }

  // Sort effects by start time
  objectEffects.sort((a, b) => a.startTime - b.startTime);

  // Function to check if two time ranges overlap
  const hasTimeOverlap = (start1, end1, start2, end2) => {
    return !(end1 <= start2 || start1 >= end2);
  };

  // Reassign rows compactly
  const rowAssignments = [];
  objectEffects.forEach((effect) => {
    // Find the lowest available row for this effect
    let assignedRow = 0;
    for (let row = 0; row < 20; row++) {
      const effectsInRow = rowAssignments.filter(
        (assigned) => assigned.row === row
      );
      const hasOverlap = effectsInRow.some((assigned) => {
        return hasTimeOverlap(
          effect.startTime,
          effect.endTime,
          assigned.startTime,
          assigned.endTime
        );
      });

      if (!hasOverlap) {
        assignedRow = row;
        break;
      }
    }

    rowAssignments.push({
      effectId: effect.id,
      row: assignedRow,
      startTime: effect.startTime,
      endTime: effect.endTime,
    });
  });

  // Update timeline effects with new row assignments
  return timelineEffects.map((effect) => {
    if (effect.objectId === objectId) {
      const assignment = rowAssignments.find((a) => a.effectId === effect.id);
      if (assignment) {
        return {
          ...effect,
          row: assignment.row,
        };
      }
    }
    return effect;
  });
};

// Utility functions
export const roundToPrecision = (num, precision = 1) => {
  return Math.round(num * Math.pow(10, precision)) / Math.pow(10, precision);
};

export const TIME_PRECISION = 3;

const HIGH_PRECISION_SETTING_KEYS = new Set([
  'duration',
  'startTime',
  'endTime',
  'animationSpeed',
  'playSpeed',
]);

export const settingsNumericPrecision = (key) =>
  HIGH_PRECISION_SETTING_KEYS.has(key) ? TIME_PRECISION : 1;

export const snapTimeToMilli = (time) => {
  const t = Number(time);
  if (!Number.isFinite(t)) return 0;
  return roundToPrecision(t, TIME_PRECISION);
};

/** @deprecated Use snapTimeToMilli — kept so older imports keep working. */
export const snapTimeToTenth = snapTimeToMilli;

/** Faster speed → shorter clip; slower speed → longer clip. */
export const durationFromSpeedChange = (
  currentDuration,
  oldSpeed,
  newSpeed
) => {
  const from = Number(oldSpeed) > 0 ? Number(oldSpeed) : 1;
  const to = Number(newSpeed) > 0 ? Number(newSpeed) : 1;
  const dur = Number(currentDuration) > 0 ? Number(currentDuration) : 0.1;
  return Math.max(0.001, roundToPrecision(dur * (from / to), TIME_PRECISION));
};

export const getEffectSpeedKey = (effect) =>
  effect?.effectType === 'playModel' ? 'playSpeed' : 'animationSpeed';

/** Shorter clip → faster speed; longer clip → slower speed. */
export const speedFromDurationChange = (
  oldDuration,
  newDuration,
  oldSpeed,
  min = 0.1,
  max = 10
) => {
  const fromDur = Number(oldDuration) > 0 ? Number(oldDuration) : 0.1;
  const toDur = Number(newDuration) > 0 ? Number(newDuration) : 0.1;
  const speed = Number(oldSpeed) > 0 ? Number(oldSpeed) : 1;
  return Math.min(
    max,
    Math.max(min, roundToPrecision(speed * (fromDur / toDur), TIME_PRECISION))
  );
};

/** Apply a new end time and keep animation speed in sync with clip length. */
export const applyDurationResizeToEffect = (
  effect,
  newEndTime,
  origin = null
) => {
  const start = snapTimeToMilli(Number(effect?.startTime) || 0);
  const minEnd = snapTimeToMilli(start + 0.001);
  const snappedEnd = Math.max(minEnd, snapTimeToMilli(Number(newEndTime)));
  const newDuration = snapTimeToMilli(snappedEnd - start);
  const liveDuration = snapTimeToMilli(
    Math.max(0.001, (Number(effect?.endTime) || start) - start)
  );
  const oldDuration = snapTimeToMilli(
    Number(origin?.duration) > 0 ? Number(origin.duration) : liveDuration
  );
  const speedKey = getEffectSpeedKey(effect);
  const maxSpeed = speedKey === 'playSpeed' ? 5 : 10;
  const oldSpeed = origin?.[speedKey] ?? effect?.[speedKey] ?? 1;

  // Ignore sub-millisecond float noise; keep original duration and speed.
  if (newDuration === oldDuration) {
    return {
      ...effect,
      startTime: start,
      endTime: snapTimeToMilli(start + oldDuration),
      duration: oldDuration,
      [speedKey]: oldSpeed,
    };
  }

  return {
    ...effect,
    startTime: start,
    endTime: snapTimeToMilli(start + newDuration),
    duration: newDuration,
    [speedKey]: speedFromDurationChange(
      oldDuration,
      newDuration,
      oldSpeed,
      0.1,
      maxSpeed
    ),
  };
};

export const parseFloatWithPrecision = (value, precision = 1) => {
  const parsed = parseFloat(value) || 0;
  return roundToPrecision(parsed, precision);
};
